import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { SILENT_WAV } from '../../utils/unlockAudio';
import { startBackgroundSession, stopBackgroundSession } from '../../services/backgroundSession';

const API = process.env.REACT_APP_BACKEND_URL;

// Plays a sequence of audio segments separated by silence pauses.
// Uses ONE persistent <audio> element primed inside the user gesture, so
// timer-driven segment changes are never blocked by the Android WebView
// autoplay policy (same pattern as BodyScanMode voices).
export function useSequencePlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [playingKey, setPlayingKey] = useState(null);
  const audioElRef = useRef(null);
  const timerRef = useRef(null);
  const stoppedRef = useRef(true);
  const requestIdRef = useRef(0);
  const bgRef = useRef(false);

  const ensureEl = useCallback(() => {
    if (!audioElRef.current) {
      const el = new window.Audio();
      el.preload = 'auto';
      audioElRef.current = el;
    }
    return audioElRef.current;
  }, []);

  // Must run synchronously inside the user gesture (before any await)
  const primeAudio = useCallback(() => {
    const el = ensureEl();
    el.muted = true;
    el.src = SILENT_WAV;
    const p = el.play();
    if (p && p.catch) p.catch(() => {});
  }, [ensureEl]);

  const endBg = useCallback(() => {
    if (bgRef.current) { bgRef.current = false; stopBackgroundSession(); }
  }, []);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    requestIdRef.current += 1;
    if (timerRef.current) clearTimeout(timerRef.current);
    const el = audioElRef.current;
    if (el) { el.onended = null; el.onerror = null; el.pause(); }
    endBg();
    setIsPlaying(false);
    setPlayingKey(null);
    setProgress({ current: 0, total: 0 });
  }, [endBg]);

  const playNext = useCallback((items, index) => {
    if (stoppedRef.current) return;
    if (index >= items.length) {
      endBg();
      setIsPlaying(false);
      setPlayingKey(null);
      setProgress({ current: 0, total: 0 });
      return;
    }
    setProgress({ current: index + 1, total: items.length });
    const el = ensureEl();
    let advanced = false;
    const advance = () => {
      if (advanced || stoppedRef.current) return;
      advanced = true;
      const pauseMs = (items[index].pause ?? 3.5) * 1000;
      timerRef.current = setTimeout(() => playNext(items, index + 1), pauseMs);
    };
    el.muted = false;
    el.onended = advance;
    el.onerror = advance;
    el.src = items[index].url.startsWith('http') ? items[index].url : `${API}${items[index].url}`;
    const p = el.play();
    if (p && p.catch) p.catch(advance);
  }, [ensureEl, endBg]);

  const beginPlayback = useCallback((items, key, label) => {
    setIsPlaying(true);
    setPlayingKey(key);
    if (!bgRef.current) { bgRef.current = true; startBackgroundSession(label); }
    playNext(items, 0);
  }, [playNext]);

  const play = useCallback(async (segments, voice, key = null) => {
    stop();
    stoppedRef.current = false;
    primeAudio();
    const myRequestId = requestIdRef.current;
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/api/edge-tts/generate`, {
        items: segments.map(s => ({ id: s.id, text: s.text })),
        voice
      });
      // Stale response guard: a stop() or a newer play() happened meanwhile
      if (requestIdRef.current !== myRequestId || stoppedRef.current) return;
      const voices = res.data.voices || {};
      const items = segments.filter(s => voices[s.id]).map(s => ({ url: voices[s.id], pause: s.pause }));
      if (items.length === 0) throw new Error('Aucun audio généré');
      beginPlayback(items, key, 'Préparation mentale en cours');
    } catch (e) {
      if (requestIdRef.current === myRequestId) {
        console.error('Sequence play error:', e);
        alert('Impossible de générer la séance. Vérifiez votre connexion.');
      }
    } finally { setIsLoading(false); }
  }, [stop, primeAudio, beginPlayback]);

  const playUrls = useCallback((urls, key = null, pause = 2) => {
    stop();
    stoppedRef.current = false;
    primeAudio();
    const items = urls.map(u => ({ url: u, pause }));
    beginPlayback(items, key, 'Préparation mentale en cours');
  }, [stop, primeAudio, beginPlayback]);

  useEffect(() => () => stop(), [stop]);

  return { isPlaying, isLoading, progress, playingKey, play, playUrls, stop };
}

export default useSequencePlayer;
